import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal, untracked } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Invoice } from "../../../pojo/Pricing";
import { PricingStore } from "../reference-set-pricing/s25.event.pricing.store";
import { PaymentsStore } from "../reference-set-pricing/payments/s25.payments.store";
import { S25PaymentRequestComponent } from "../reference-set-pricing/payments/s25.payment.request.component";
import { EventPricingService } from "../reference-set-pricing/s25.event.pricing.service";
import { OrgSeededPricingService } from "./org-seeded-pricing.service";

/**
 * Hosts the payment-request compose panel for one row of the organization's Invoices tab.
 *
 * `s25-ng-payment-request` takes no inputs: it reads the event, the invoice and the organization
 * from `PaymentsStore`, which in turn reads `PricingStore`. Both stores are component-provided
 * rather than root, so this host provides its own pair and seeds them with the row's event, name
 * and invoice. The panel opens on that alone and shows its own spinner until the billing arrives.
 * The stores are marked compose-only, which keeps the pricing page's own reads out of it.
 *
 * A row with a payment still owed opens as a reminder against it, writing nothing. A row whose
 * payments are all settled but still carries a balance opens as a request for the remainder, which
 * does log a payment once the email goes.
 */
@TypeManagerDecorator("s25-ng-org-invoice-reminder")
@Component({
    selector: "s25-ng-org-invoice-reminder",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25PaymentRequestComponent],
    providers: [{ provide: EventPricingService, useClass: OrgSeededPricingService }, PricingStore, PaymentsStore],
    template: `<s25-ng-payment-request />`,
})
export class S25OrgInvoiceReminderComponent {
    private readonly pricingStore = inject(PricingStore);
    private readonly paymentsStore = inject(PaymentsStore);

    readonly orgId = input.required<number>();
    readonly eventId = input.required<number>();
    readonly eventName = input.required<string>();
    readonly invoiceId = input.required<number>();
    readonly paymentDetailId = input<number | undefined>(undefined);

    readonly closed = output<Invoice[]>();

    private readonly hasOpened = signal(false);

    constructor() {
        this.pricingStore.setComposeOnly(true);

        effect(() => this.pricingStore.setEventId(this.eventId()));
        effect(() => this.pricingStore.setEvBillId(this.invoiceId()));
        effect(() => this.paymentsStore.setEventName(this.eventName()));

        effect(() => {
            const orgId = this.orgId();
            const paymentDetailId = this.paymentDetailId();

            untracked(() => {
                if (this.paymentsStore.requestOrgId()) return;
                if (paymentDetailId) this.paymentsStore.openReminder(orgId, paymentDetailId);
                else this.paymentsStore.openRequest(orgId);
            });
        });

        effect(() => {
            if (!!this.paymentsStore.requestOrgId()) return untracked(() => this.hasOpened.set(true));
            if (untracked(this.hasOpened)) this.closed.emit(untracked(this.pricingStore.invoices));
        });
    }
}
